import React, { useContext, useEffect, useMemo } from 'react';
import { defineMessages, T, t } from '@sm/intl';
import { Accordion } from '@wds/accordion';
import { Box } from '@wds/box';
import { IconCaretDown } from '@wds/icons';
import { List } from '@wds/list';
import { Menu } from '@wds/menu';
import { Responsive } from '@wds/responsive';
import {
  generateMetricsAttribute,
  MetricsTracker,
  USER_EVENTS,
} from '@sm/metrics';
import { NavButton } from '../../nav-button';
import { SubMenuItem } from '../../mobile-nav/SubMenuItem';
import getMyTeamMenuItems from '../../../../../../helpers/TeamMenuHelpers';
import { useHeaderTheme } from '../../../theme';
import HeaderContext from '../../../HeaderContext';
import useStyles from '../useStyles';

import { useAuthQueryLazyQuery } from './team-menu.generated';
import { RbacPermission, RbacPermissionType } from '../../../../../../types';

const COPY = defineMessages({
  MY_TEAM: {
    id: 'TeamMenu.myTeam',
    defaultMessage: 'My Team',
    description: '[Type: Label][Vis.: low] - My team section of nav bar',
  },
});

const TeamMenu = () => {
  const theme = useHeaderTheme();
  const classes = useStyles({ theme });

  const { session, pageURL, pageName } = useContext(HeaderContext);

  const [fetch, { loading, data }] = useAuthQueryLazyQuery({
    variables: {
      permissions: [
        RbacPermissionType.TeamSummaryView,
        RbacPermissionType.UsersManageAccounts,
        RbacPermissionType.UsersInvite,
        RbacPermissionType.DivisionsView,
        RbacPermissionType.WorkgroupsView,
        RbacPermissionType.ActivityView,
        RbacPermissionType.OfflineDevicesManage,
        RbacPermissionType.AudienceCreditsManage,
        RbacPermissionType.AdminDashboardView,
        RbacPermissionType.LibraryView,
      ],
    },
  });

  useEffect(() => {
    fetch();
  }, []);

  const permissionData = useMemo(
    () =>
      data?.rbacPermissions.reduce(
        (acc: { [key: string]: boolean }, value: RbacPermission) => {
          acc[value.permission] = value.isAuthorized;
          return acc;
        },
        {}
      ) || {},
    [data]
  );

  const permissions = {
    teamSummary: permissionData[RbacPermissionType.TeamSummaryView] ?? false,
    manageUsers:
      permissionData[RbacPermissionType.UsersManageAccounts] ?? false,
    addUsers: permissionData[RbacPermissionType.UsersInvite] ?? false,
    division: permissionData[RbacPermissionType.DivisionsView] ?? false,
    workgroup: permissionData[RbacPermissionType.WorkgroupsView] ?? false,
    activity: permissionData[RbacPermissionType.ActivityView] ?? false,
    library: permissionData[RbacPermissionType.LibraryView] ?? false,
    offlineDevices:
      permissionData[RbacPermissionType.OfflineDevicesManage] ?? false,
    adminDashboard:
      permissionData[RbacPermissionType.AdminDashboardView] ?? false,
    audienceCreditsManage:
      permissionData[RbacPermissionType.AudienceCreditsManage] ?? false,
  };

  if (loading) return null;
  if (!data?.user) return null;
  const user = data?.user;

  const TrackMenuEvents = (opened: boolean) => {
    MetricsTracker.track({
      name: USER_EVENTS.ELEMENT_INTERACTION,
      data: {
        url: pageURL,
        pageName,
        amplitudeEvent: 'header clicked menu',
        menuName: 'team',
        opened,
      },
    });
  };

  const GenerateMetricsNavigationEvent = (navItem: String) =>
    generateMetricsAttribute({
      data: {
        url: pageURL,
        pageName,
        amplitudeEvent: 'header clicked navigation',
        navItem,
      },
    });

  const teamMembership = user.teamMembership ?? {
    isAdmin: false,
    isEnterprise: false,
    memberType: '',
  };
  const canSeeTeamTab = teamMembership.isAdmin || teamMembership.isEnterprise;

  const title = t(COPY.MY_TEAM);
  const menuItems = getMyTeamMenuItems({ user, session, permissions });

  return canSeeTeamTab ? (
    <Responsive minWidth="xl">
      {matches =>
        matches ? (
          <Menu
            placement="bottom-left"
            noRules
            dense
            menuTrigger={
              <NavButton label="Open My Team menu">
                <T desc={COPY.MY_TEAM} />
                <Box ml={2}>
                  <IconCaretDown title="Dropdown arrow" />
                </Box>
              </NavButton>
            }
            key={title}
            elevation={9999}
            onChange={showMenu => {
              TrackMenuEvents(showMenu);
            }}
          >
            {menuItems.map(item => {
              return (
                <Menu.Item
                  key={`${item.name}-teamMenu`}
                  data-sm-metrics={GenerateMetricsNavigationEvent(item.navItem)}
                >
                  <a
                    href={item.path}
                    color="dark"
                    className={classes.teamMenuItem}
                  >
                    {item.name}
                  </a>
                </Menu.Item>
              );
            })}
          </Menu>
        ) : (
          <Accordion
            key="accordion-MyTeam"
            onChange={e => TrackMenuEvents(!e.length)}
          >
            <Accordion.Item title={title}>
              <List transparent>
                {menuItems.map(item => {
                  return (
                    <SubMenuItem
                      key={item.name}
                      metrics={GenerateMetricsNavigationEvent(item.navItem)}
                      {...item}
                    />
                  );
                })}
              </List>
            </Accordion.Item>
          </Accordion>
        )
      }
    </Responsive>
  ) : null;
};

export default TeamMenu;
export { TeamMenu };
